// source: https://github.com/sersavan/shadcn-multi-select-component
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckIcon,
  ChevronDown,
  PlusIcon,
  XIcon,
  WandSparkles,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/Separator";
import { Badge } from "@/components/ui/Badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/Command";

/**
 * Animation types and configurations
 */
interface AnimationConfig {
  /** Badge animation type */
  badgeAnimation?: "lift" | "pulse" | "wiggle" | "fade" | "slide" | "none";
  /** Popover animation type */
  popoverAnimation?: "scale" | "slide" | "fade" | "flip" | "none";
  /** Option hover animation type */
  optionHoverAnimation?: "highlight" | "scale" | "glow" | "none";
  /** Animation duration in seconds */
  duration?: number;
  /** Animation delay in seconds */
  delay?: number;
}

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva("m-1 transition-all duration-300 ease-in-out", {
  variants: {
    variant: {
      default: "border-foreground/10 text-foreground bg-card hover:bg-card/80",
      secondary:
        "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
      destructive:
        "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
      inverted: "inverted",
    },
    badgeAnimation: {
      lift: "hover:-translate-y-1 hover:scale-110",
      pulse: "hover:animate-pulse",
      wiggle: "hover:animate-wiggle",
      fade: "hover:opacity-80",
      slide: "hover:translate-x-1",
      none: "",
    },
  },
  defaultVariants: {
    variant: "default",
    // A selected-value chip sits inside a fixed-height field: lifting and
    // scaling it on hover pushes it outside the trigger's bounds. Opt in per
    // instance via animationConfig.badgeAnimation instead.
    badgeAnimation: "none",
  },
});

/**
 * Option interface for MultiSelect component
 */
interface MultiSelectOption {
  /** The text to display for the option. */
  label: string;
  /** The unique value associated with the option. */
  value: string;
  /** Optional icon component to display alongside the option. */
  icon?: React.ComponentType<{ className?: string }>;
  /**
   * Optional secondary explanation shown under the label. Use for non-obvious
   * values (e.g. Manual vs Captured) so the meaning is visible while choosing.
   */
  description?: string;
  /** Whether this option is disabled */
  disabled?: boolean;
  /** Optional presentation class for the option row. */
  className?: string;
  /** Custom styling for the option */
  style?: {
    /** Custom badge color */
    badgeColor?: string;
    /** Custom icon color */
    iconColor?: string;
    /** Gradient background for badge */
    gradient?: string;
  };
}

/**
 * Group interface for organizing options
 */
interface MultiSelectGroup {
  /** Group heading */
  heading: string;
  /** Optional icon shown beside the group heading. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Options in this group */
  options: MultiSelectOption[];
}

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "animationConfig">,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects or groups to be displayed in the multi-select component.
   */
  options: MultiSelectOption[] | MultiSelectGroup[];
  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * The selected values when the component is controlled by its caller.
   * When omitted, the component manages its own selection initialized from
   * `defaultValue`.
   */
  value?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Advanced animation configuration for different component parts.
   * Optional, allows fine-tuning of various animation effects.
   */
  animationConfig?: AnimationConfig;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * If true, disables the select all functionality.
   * Optional, defaults to false.
   */
  hideSelectAll?: boolean;

  /**
   * If true, shows search functionality in the popover.
   * If false, hides the search input completely.
   * Optional, defaults to true.
   */
  searchable?: boolean;

  /**
   * Custom empty state message when no options match search.
   * Optional, defaults to "No results found."
   */
  emptyIndicator?: React.ReactNode;

  /**
   * If true, allows the component to grow and shrink with its content.
   * If false, uses fixed width behavior.
   * Optional, defaults to false.
   */
  autoSize?: boolean;

  /**
   * If true, shows badges in a single line with horizontal scroll.
   * If false, badges wrap to multiple lines.
   * Optional, defaults to false.
   */
  singleLine?: boolean;

  /**
   * Custom CSS class for the popover content.
   * Optional, can be used to customize popover appearance.
   */
  popoverClassName?: string;

  /**
   * Custom CSS class for the selected-value badges in the trigger. Useful when
   * the selections are prose (names, emails) rather than tokens, and the
   * badge's default mono/uppercase treatment reads wrong.
   * Optional.
   */
  badgeClassName?: string;

  /**
   * Optionally replace a related set of selected values with one compact
   * summary in the trigger. The values remain selected and available in the
   * menu; only their trigger representation changes.
   */
  collapseSelectedValues?: (
    selectedValues: string[],
  ) => { values: string[]; summary: React.ReactNode } | null;

  /**
   * If true, disables the component completely.
   * Optional, defaults to false.
   */
  disabled?: boolean;

  /**
   * Responsive configuration for different screen sizes.
   * Allows customizing maxCount and other properties based on viewport.
   * Can be boolean true for default responsive behavior or an object for custom configuration.
   */
  responsive?:
    | boolean
    | {
        /** Configuration for mobile devices (< 640px) */
        mobile?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
        /** Configuration for tablet devices (640px - 1024px) */
        tablet?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
        /** Configuration for desktop devices (> 1024px) */
        desktop?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
      };

  /**
   * Minimum width for the component.
   * Optional, defaults to auto-sizing based on content.
   * When set, component will not shrink below this width.
   */
  minWidth?: string;

  /**
   * Maximum width for the component.
   * Optional, defaults to 100% of container.
   * Component will not exceed container boundaries.
   */
  maxWidth?: string;

  /**
   * If true, automatically removes duplicate options based on their value.
   * Optional, defaults to false (shows warning in dev mode instead).
   */
  deduplicateOptions?: boolean;

  /**
   * If true, the component will reset its internal state when defaultValue changes.
   * Useful for React Hook Form integration and form reset functionality.
   * Optional, defaults to true.
   */
  resetOnDefaultValueChange?: boolean;

  /**
   * If true, automatically closes the popover after selecting an option.
   * Useful for single-selection-like behavior or mobile UX.
   * Optional, defaults to false.
   */
  closeOnSelect?: boolean;

  /**
   * If true, allows users to create new values from the search input.
   * When the search query has content and does not exactly match an existing
   * option (case-insensitive), a "Create '<text>'" item is shown at the top
   * of the dropdown. Selecting it adds the trimmed search text to the
   * selected values. Selected values that are not present in `options` are
   * still rendered as badges using the value as the label.
   * Optional, defaults to false.
   */
  creatable?: boolean;

  /**
   * If true, a created value only counts as existing when it matches an
   * option or selection exactly. Use for case-sensitive tokens, where `Read`
   * and `read` are different values.
   * Optional, defaults to false.
   */
  caseSensitiveCreate?: boolean;
}

/**
 * Imperative methods exposed through ref
 */
export interface MultiSelectRef {
  /**
   * Programmatically reset the component to its default value
   */
  reset: () => void;
  /**
   * Get current selected values
   */
  getSelectedValues: () => string[];
  /**
   * Set selected values programmatically
   */
  setSelectedValues: (values: string[]) => void;
  /**
   * Clear all selected values
   */
  clear: () => void;
  /**
   * Focus the component
   */
  focus: () => void;
}

function MultiSelectOptionItem({
  option,
  isSelected,
  onToggle,
}: {
  option: MultiSelectOption;
  isSelected: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <CommandItem
      onSelect={onToggle}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled}
      aria-label={`${option.label}${
        isSelected ? ", selected" : ", not selected"
      }${option.disabled ? ", disabled" : ""}${
        option.description ? `. ${option.description}` : ""
      }`}
      className={cn(
        "cursor-pointer",
        option.disabled && "cursor-not-allowed opacity-60",
        option.className,
      )}
      disabled={option.disabled}
    >
      <div
        className={cn(
          "border-primary mr-2 flex h-4 w-4 shrink-0 items-center justify-center border",
          isSelected
            ? "bg-primary text-primary-foreground"
            : "opacity-50 [&_svg]:invisible",
        )}
        aria-hidden="true"
      >
        <CheckIcon className="h-4 w-4 text-current" />
      </div>
      {option.icon && (
        <option.icon
          className="text-muted-foreground mr-2 h-4 w-4 shrink-0"
          aria-hidden="true"
        />
      )}
      <div className="flex min-w-0 flex-col">
        <span>{option.label}</span>
        {option.description && (
          <span className="text-muted-foreground text-xs leading-tight">
            {option.description}
          </span>
        )}
      </div>
    </CommandItem>
  );
}

export const MultiSelect = React.forwardRef<MultiSelectRef, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      value,
      placeholder = "Select options",
      animation = 0,
      animationConfig,
      maxCount = 3,
      modalPopover = false,
      className,
      hideSelectAll = false,
      searchable = true,
      emptyIndicator,
      autoSize = false,
      singleLine = false,
      popoverClassName,
      badgeClassName,
      collapseSelectedValues,
      disabled = false,
      responsive,
      minWidth,
      maxWidth,
      deduplicateOptions = false,
      resetOnDefaultValueChange = true,
      closeOnSelect = false,
      creatable = false,
      caseSensitiveCreate = false,
      ...props
    },
    ref,
  ) => {
    const [uncontrolledSelectedValues, setUncontrolledSelectedValues] =
      React.useState<string[]>(defaultValue);
    const isControlled = value !== undefined;
    const selectedValues = value ?? uncontrolledSelectedValues;

    const updateSelectedValues = React.useCallback(
      (next: string[]) => {
        if (!isControlled) {
          setUncontrolledSelectedValues(next);
        }
        onValueChange(next);
      },
      [isControlled, onValueChange],
    );
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");

    const [politeMessage, setPoliteMessage] = React.useState("");
    const [assertiveMessage, setAssertiveMessage] = React.useState("");
    const prevSelectedCount = React.useRef(selectedValues.length);
    const prevIsOpen = React.useRef(isPopoverOpen);
    const prevSearchValue = React.useRef(searchValue);

    const politeTimerRef =
      React.useRef<ReturnType<typeof setTimeout>>(undefined);
    const assertiveTimerRef =
      React.useRef<ReturnType<typeof setTimeout>>(undefined);

    const announce = React.useCallback(
      (message: string, priority: "polite" | "assertive" = "polite") => {
        if (priority === "assertive") {
          setAssertiveMessage(message);
          clearTimeout(assertiveTimerRef.current);
          assertiveTimerRef.current = setTimeout(
            () => setAssertiveMessage(""),
            100,
          );
        } else {
          setPoliteMessage(message);
          clearTimeout(politeTimerRef.current);
          politeTimerRef.current = setTimeout(() => setPoliteMessage(""), 100);
        }
      },
      [],
    );

    React.useEffect(() => {
      return () => {
        clearTimeout(politeTimerRef.current);
        clearTimeout(assertiveTimerRef.current);
      };
    }, []);

    const multiSelectId = React.useId();
    const listboxId = `${multiSelectId}-listbox`;
    const triggerDescriptionId = `${multiSelectId}-description`;
    const selectedCountId = `${multiSelectId}-count`;

    const prevDefaultValueRef = React.useRef<string[]>(defaultValue);

    const isGroupedOptions = React.useCallback(
      (
        opts: MultiSelectOption[] | MultiSelectGroup[],
      ): opts is MultiSelectGroup[] => {
        return opts.length > 0 && "heading" in opts[0]!;
      },
      [],
    );

    const arraysEqual = React.useCallback(
      (a: string[], b: string[]): boolean => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, index) => val === sortedB[index]);
      },
      [],
    );

    const resetToDefault = React.useCallback(() => {
      if (!isControlled) {
        setUncontrolledSelectedValues(defaultValue);
      }
      setIsPopoverOpen(false);
      setSearchValue("");
      onValueChange(defaultValue);
    }, [defaultValue, isControlled, onValueChange]);

    const buttonRef = React.useRef<HTMLButtonElement>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        reset: resetToDefault,
        getSelectedValues: () => selectedValues,
        setSelectedValues: (values: string[]) => {
          updateSelectedValues(values);
        },
        clear: () => {
          updateSelectedValues([]);
        },
        focus: () => {
          if (buttonRef.current) {
            buttonRef.current.focus();
            const originalOutline = buttonRef.current.style.outline;
            const originalOutlineOffset = buttonRef.current.style.outlineOffset;
            buttonRef.current.style.outline = "2px solid hsl(var(--ring))";
            buttonRef.current.style.outlineOffset = "2px";
            setTimeout(() => {
              if (buttonRef.current) {
                buttonRef.current.style.outline = originalOutline;
                buttonRef.current.style.outlineOffset = originalOutlineOffset;
              }
            }, 1000);
          }
        },
      }),
      [resetToDefault, selectedValues, updateSelectedValues],
    );

    const [screenSize, setScreenSize] = React.useState<
      "mobile" | "tablet" | "desktop"
    >("desktop");

    React.useEffect(() => {
      if (typeof window === "undefined") return;
      const handleResize = () => {
        const width = window.innerWidth;
        if (width < 640) {
          setScreenSize("mobile");
        } else if (width < 1024) {
          setScreenSize("tablet");
        } else {
          setScreenSize("desktop");
        }
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => {
        if (typeof window !== "undefined") {
          window.removeEventListener("resize", handleResize);
        }
      };
    }, []);

    const getResponsiveSettings = () => {
      if (!responsive) {
        return {
          maxCount: maxCount,
          hideIcons: false,
          compactMode: false,
        };
      }
      if (responsive === true) {
        const defaultResponsive = {
          mobile: { maxCount: 2, hideIcons: false, compactMode: true },
          tablet: { maxCount: 4, hideIcons: false, compactMode: false },
          desktop: { maxCount: 6, hideIcons: false, compactMode: false },
        };
        const currentSettings = defaultResponsive[screenSize];
        return {
          maxCount: currentSettings?.maxCount ?? maxCount,
          hideIcons: currentSettings?.hideIcons ?? false,
          compactMode: currentSettings?.compactMode ?? false,
        };
      }
      const currentSettings = responsive[screenSize];
      return {
        maxCount: currentSettings?.maxCount ?? maxCount,
        hideIcons: currentSettings?.hideIcons ?? false,
        compactMode: currentSettings?.compactMode ?? false,
      };
    };

    const responsiveSettings = getResponsiveSettings();

    const getBadgeAnimationClass = () => {
      if (animationConfig?.badgeAnimation) {
        switch (animationConfig.badgeAnimation) {
          case "lift":
            return isAnimating
              ? "animate-pulse"
              : "hover:-translate-y-1 hover:scale-110";
          case "pulse":
            return "hover:animate-pulse";
          case "wiggle":
            return "hover:animate-wiggle";
          case "fade":
            return "hover:opacity-80";
          case "slide":
            return "hover:translate-x-1";
          case "none":
            return "";
          default:
            return "";
        }
      }
      return isAnimating ? "animate-pulse" : "";
    };

    const getPopoverAnimationClass = () => {
      if (animationConfig?.popoverAnimation) {
        switch (animationConfig.popoverAnimation) {
          case "scale":
            return "animate-scaleIn";
          case "slide":
            return "animate-slideInDown";
          case "fade":
            return "animate-fadeIn";
          case "flip":
            return "animate-flipIn";
          case "none":
            return "";
          default:
            return "";
        }
      }
      return "";
    };

    const getAllOptions = React.useCallback((): MultiSelectOption[] => {
      if (options.length === 0) return [];
      let allOptions: MultiSelectOption[];
      if (isGroupedOptions(options)) {
        allOptions = options.flatMap((group) => group.options);
      } else {
        allOptions = options;
      }
      const valueSet = new Set<string>();
      const duplicates: string[] = [];
      const uniqueOptions: MultiSelectOption[] = [];
      allOptions.forEach((option) => {
        if (valueSet.has(option.value)) {
          duplicates.push(option.value);
          if (!deduplicateOptions) {
            uniqueOptions.push(option);
          }
        } else {
          valueSet.add(option.value);
          uniqueOptions.push(option);
        }
      });
      if (process.env.NODE_ENV === "development" && duplicates.length > 0) {
        const action = deduplicateOptions
          ? "automatically removed"
          : "detected";
        console.warn(
          `MultiSelect: Duplicate option values ${action}: ${duplicates.join(
            ", ",
          )}. ` +
            `${
              deduplicateOptions
                ? "Duplicates have been removed automatically."
                : "This may cause unexpected behavior. Consider setting 'deduplicateOptions={true}' or ensure all option values are unique."
            }`,
        );
      }
      return deduplicateOptions ? uniqueOptions : allOptions;
    }, [options, deduplicateOptions, isGroupedOptions]);

    const getOptionByValue = React.useCallback(
      (value: string): MultiSelectOption | undefined => {
        const option = getAllOptions().find((option) => option.value === value);
        if (option) return option;
        if (creatable) {
          return { label: value, value };
        }
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `MultiSelect: Option with value "${value}" not found in options list`,
          );
        }
        return undefined;
      },
      [getAllOptions, creatable],
    );

    const optionMatchesSearch = React.useCallback(
      (option: MultiSelectOption, needle: string) => {
        const q = needle.toLowerCase();
        return (
          option.label.toLowerCase().includes(q) ||
          option.value.toLowerCase().includes(q) ||
          (option.description?.toLowerCase().includes(q) ?? false)
        );
      },
      [],
    );

    const filteredOptions = React.useMemo(() => {
      if (!searchable || !searchValue) return options;
      if (options.length === 0) return [];
      if (isGroupedOptions(options)) {
        return options
          .map((group) => ({
            ...group,
            options: group.options.filter((option) =>
              optionMatchesSearch(option, searchValue),
            ),
          }))
          .filter((group) => group.options.length > 0);
      }
      return options.filter((option) =>
        optionMatchesSearch(option, searchValue),
      );
    }, [
      options,
      searchValue,
      searchable,
      isGroupedOptions,
      optionMatchesSearch,
    ]);

    // We filter `options` into `filteredOptions` ourselves, so cmdk's built-in
    // filtering is disabled (see <Command shouldFilter={false}> below). Leaving
    // both filters enabled makes them race over a shrinking item set as the user
    // types, which intermittently shows the empty state for valid matches and
    // can leave it stuck when characters are deleted (AIS-84). Because
    // shouldFilter={false} also disables cmdk's <CommandEmpty>, the empty state
    // is derived from filteredOptions instead.
    const filteredOptionCount = React.useMemo(() => {
      if (isGroupedOptions(filteredOptions)) {
        return filteredOptions.reduce(
          (count, group) => count + group.options.length,
          0,
        );
      }
      return filteredOptions.length;
    }, [filteredOptions, isGroupedOptions]);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        updateSelectedValues(newSelectedValues);
      }
    };

    const toggleOption = (optionValue: string) => {
      if (disabled) return;
      const option = getOptionByValue(optionValue);
      if (option?.disabled) return;
      const newSelectedValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((value) => value !== optionValue)
        : [...selectedValues, optionValue];
      updateSelectedValues(newSelectedValues);
      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    // A disabled option cannot be newly selected from the menu, but an
    // already-selected option must still be removable. This lets callers show
    // legacy or unavailable values without making them permanent selections.
    const removeOption = (optionValue: string) => {
      if (disabled) return;
      const newSelectedValues = selectedValues.filter(
        (value) => value !== optionValue,
      );
      updateSelectedValues(newSelectedValues);
    };

    const trimmedSearchValue = searchValue.trim();
    const canCreateFromSearch = React.useMemo(() => {
      if (!creatable || !trimmedSearchValue) return false;
      const fold = (text: string) =>
        caseSensitiveCreate ? text : text.toLowerCase();
      const needle = fold(trimmedSearchValue);
      const matchesExisting = getAllOptions().some(
        (option) =>
          fold(option.value) === needle || fold(option.label) === needle,
      );
      if (matchesExisting) return false;
      return !selectedValues.some((value) => fold(value) === needle);
    }, [
      creatable,
      caseSensitiveCreate,
      trimmedSearchValue,
      getAllOptions,
      selectedValues,
    ]);

    const handleCreate = () => {
      if (disabled || !canCreateFromSearch) return;
      const newSelectedValues = [...selectedValues, trimmedSearchValue];
      updateSelectedValues(newSelectedValues);
      setSearchValue("");
      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    const handleClear = () => {
      if (disabled) return;
      updateSelectedValues([]);
    };

    const collapsedSelection = collapseSelectedValues?.(selectedValues);
    const collapsedValues = new Set(collapsedSelection?.values);
    const visibleSelectedValues = selectedValues.filter(
      (value) => !collapsedValues.has(value),
    );

    const handleTogglePopover = () => {
      if (disabled) return;
      setIsPopoverOpen((prev) => !prev);
    };

    const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      handleTogglePopover();
    };

    const stopNestedActionKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.stopPropagation();
      }
    };

    const clearExtraOptions = () => {
      if (disabled) return;
      const retainedVisibleValues = new Set(
        visibleSelectedValues.slice(0, responsiveSettings.maxCount),
      );
      const newSelectedValues = selectedValues.filter(
        (value) =>
          collapsedValues.has(value) || retainedVisibleValues.has(value),
      );
      updateSelectedValues(newSelectedValues);
    };

    const toggleAll = () => {
      if (disabled) return;
      const allOptions = getAllOptions().filter((option) => !option.disabled);
      if (selectedValues.length === allOptions.length) {
        handleClear();
      } else {
        const allValues = allOptions.map((option) => option.value);
        updateSelectedValues(allValues);
      }

      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    React.useEffect(() => {
      if (isControlled || !resetOnDefaultValueChange) return;
      const prevDefaultValue = prevDefaultValueRef.current;
      if (!arraysEqual(prevDefaultValue, defaultValue)) {
        if (!arraysEqual(selectedValues, defaultValue)) {
          setUncontrolledSelectedValues(defaultValue);
        }
        prevDefaultValueRef.current = [...defaultValue];
      }
    }, [
      defaultValue,
      selectedValues,
      arraysEqual,
      isControlled,
      resetOnDefaultValueChange,
    ]);

    const getWidthConstraints = () => {
      const defaultMinWidth = screenSize === "mobile" ? "0px" : "200px";
      const effectiveMinWidth = minWidth || defaultMinWidth;
      const effectiveMaxWidth = maxWidth || "100%";
      return {
        minWidth: effectiveMinWidth,
        maxWidth: effectiveMaxWidth,
        width: autoSize ? "auto" : "100%",
      };
    };

    const widthConstraints = getWidthConstraints();

    React.useEffect(() => {
      if (!isPopoverOpen) {
        setSearchValue("");
      }
    }, [isPopoverOpen]);

    React.useEffect(() => {
      const selectedCount = selectedValues.length;
      const allOptions = getAllOptions();
      const totalOptions = allOptions.filter((opt) => !opt.disabled).length;
      if (selectedCount !== prevSelectedCount.current) {
        const diff = selectedCount - prevSelectedCount.current;
        if (diff > 0) {
          const addedItems = selectedValues.slice(-diff);
          const addedLabels = addedItems
            .map(
              (value) => allOptions.find((opt) => opt.value === value)?.label,
            )
            .filter(Boolean);

          if (addedLabels.length === 1) {
            announce(
              `${addedLabels[0]} selected. ${selectedCount} of ${totalOptions} options selected.`,
            );
          } else {
            announce(
              `${addedLabels.length} options selected. ${selectedCount} of ${totalOptions} total selected.`,
            );
          }
        } else if (diff < 0) {
          announce(
            `Option removed. ${selectedCount} of ${totalOptions} options selected.`,
          );
        }
        prevSelectedCount.current = selectedCount;
      }

      if (isPopoverOpen !== prevIsOpen.current) {
        if (isPopoverOpen) {
          announce(
            `Dropdown opened. ${totalOptions} options available. Use arrow keys to navigate.`,
          );
        } else {
          announce("Dropdown closed.");
        }
        prevIsOpen.current = isPopoverOpen;
      }

      if (
        searchValue !== prevSearchValue.current &&
        searchValue !== undefined
      ) {
        if (searchValue && isPopoverOpen) {
          const filteredCount = allOptions.filter(
            (opt) =>
              opt.label.toLowerCase().includes(searchValue.toLowerCase()) ||
              opt.value.toLowerCase().includes(searchValue.toLowerCase()),
          ).length;

          announce(
            `${filteredCount} option${
              filteredCount === 1 ? "" : "s"
            } found for "${searchValue}"`,
          );
        }
        prevSearchValue.current = searchValue;
      }
    }, [selectedValues, isPopoverOpen, searchValue, announce, getAllOptions]);

    return (
      <>
        <div className="sr-only">
          <div aria-live="polite" aria-atomic="true" role="status">
            {politeMessage}
          </div>
          <div aria-live="assertive" aria-atomic="true" role="alert">
            {assertiveMessage}
          </div>
        </div>

        <Popover
          open={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
          modal={modalPopover}
        >
          <div id={triggerDescriptionId} className="sr-only">
            Multi-select dropdown. Use arrow keys to navigate, Enter to select,
            and Escape to close.
          </div>
          <div id={selectedCountId} className="sr-only" aria-live="polite">
            {selectedValues.length === 0
              ? "No options selected"
              : `${selectedValues.length} option${
                  selectedValues.length === 1 ? "" : "s"
                } selected: ${selectedValues
                  .map((value) => getOptionByValue(value)?.label)
                  .filter(Boolean)
                  .join(", ")}`}
          </div>

          <div
            className={cn(
              "relative flex h-auto min-h-[42px] items-center justify-between border bg-inherit p-1 hover:bg-inherit",
              autoSize ? "w-auto" : "w-full",
              singleLine && "h-[42px] min-h-0",
              responsiveSettings.compactMode && "min-h-8 text-sm",
              screenSize === "mobile" && "min-h-12 text-base",
              disabled && "cursor-not-allowed opacity-50",
              className,
            )}
            style={{
              ...widthConstraints,
              maxWidth: `min(${widthConstraints.maxWidth}, 100%)`,
            }}
          >
            <PopoverTrigger asChild>
              <button
                ref={buttonRef}
                type="button"
                {...props}
                onKeyDown={handleTriggerKeyDown}
                role="combobox"
                disabled={disabled}
                aria-expanded={isPopoverOpen}
                aria-haspopup="listbox"
                aria-controls={isPopoverOpen ? listboxId : undefined}
                aria-describedby={`${triggerDescriptionId} ${selectedCountId}`}
                aria-label={`Multi-select: ${selectedValues.length} of ${
                  getAllOptions().length
                } options selected. ${placeholder}`}
                inert={disabled || undefined}
                className="absolute inset-0 z-0 cursor-pointer focus:ring-2 focus:ring-inset focus:outline-none disabled:cursor-not-allowed"
              />
            </PopoverTrigger>
            <div
              className="pointer-events-none relative z-10 flex w-full items-center justify-between"
              onClick={handleTogglePopover}
            >
              {selectedValues.length > 0 ? (
                <>
                  <div
                    className={cn(
                      "flex min-w-0 flex-1 items-center gap-1",
                      singleLine
                        ? "multiselect-singleline-scroll overflow-x-auto overflow-y-hidden"
                        : "flex-wrap",
                      responsiveSettings.compactMode && "gap-0.5",
                    )}
                    style={{}}
                  >
                    {collapsedSelection?.summary && (
                      <div className="pointer-events-auto">
                        {collapsedSelection.summary}
                      </div>
                    )}
                    {visibleSelectedValues
                      .slice(0, responsiveSettings.maxCount)
                      .map((value) => {
                        const option = getOptionByValue(value);
                        const IconComponent = option?.icon;
                        const customStyle = option?.style;
                        if (!option) {
                          return null;
                        }
                        const badgeStyle: React.CSSProperties = {
                          animationDuration: `${animation}s`,
                          ...(customStyle?.badgeColor && {
                            backgroundColor: customStyle.badgeColor,
                          }),
                          ...(customStyle?.gradient && {
                            background: customStyle.gradient,
                            color: "white",
                          }),
                        };
                        return (
                          <Badge
                            key={value}
                            className={cn(
                              getBadgeAnimationClass(),
                              multiSelectVariants({ variant }),
                              customStyle?.gradient &&
                                "border-transparent text-white",
                              responsiveSettings.compactMode &&
                                "px-1.5 py-0.5 text-xs",
                              // Long labels truncate instead of forcing the
                              // trigger row past the button's right edge
                              // (DNO-552).
                              singleLine
                                ? "shrink-0 whitespace-nowrap"
                                : "min-w-0 max-w-full shrink",
                              screenSize === "mobile" &&
                                "max-w-[120px] truncate",
                              "pointer-events-none",
                              badgeClassName,
                            )}
                            style={{
                              ...badgeStyle,
                              animationDuration: `${
                                animationConfig?.duration || animation
                              }s`,
                              animationDelay: `${animationConfig?.delay || 0}s`,
                            }}
                          >
                            {IconComponent && !responsiveSettings.hideIcons && (
                              <IconComponent
                                className={cn(
                                  "mr-2 h-4 w-4 shrink-0",
                                  responsiveSettings.compactMode &&
                                    "mr-1 h-3 w-3",
                                  customStyle?.iconColor && "text-current",
                                )}
                                {...(customStyle?.iconColor && {
                                  style: { color: customStyle.iconColor },
                                })}
                              />
                            )}
                            <span className="min-w-0 truncate">
                              {option.label}
                            </span>
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={(event) => {
                                event.stopPropagation();
                                removeOption(value);
                              }}
                              onKeyDown={stopNestedActionKeyDown}
                              aria-label={`Remove ${option.label} from selection`}
                              className="pointer-events-auto flex h-4 w-4 shrink-0 cursor-pointer hover:bg-white/20 focus:ring-1 focus:ring-white/50 focus:outline-none"
                            >
                              <XIcon
                                className={cn(
                                  "text-muted-foreground/80 m-auto h-4 w-4",
                                  responsiveSettings.compactMode &&
                                    "h-2.5 w-2.5",
                                )}
                              />
                            </button>
                          </Badge>
                        );
                      })
                      .filter(Boolean)}
                    {visibleSelectedValues.length >
                      responsiveSettings.maxCount && (
                      <Badge
                        className={cn(
                          "text-foreground border-foreground/1 bg-transparent hover:bg-transparent",
                          getBadgeAnimationClass(),
                          multiSelectVariants({ variant }),
                          responsiveSettings.compactMode &&
                            "px-1.5 py-0.5 text-xs",
                          singleLine && "shrink-0 whitespace-nowrap",
                          "pointer-events-none",
                          badgeClassName,
                        )}
                        style={{
                          animationDuration: `${
                            animationConfig?.duration || animation
                          }s`,
                          animationDelay: `${animationConfig?.delay || 0}s`,
                        }}
                      >
                        {`+ ${
                          visibleSelectedValues.length -
                          responsiveSettings.maxCount
                        } more`}
                        <button
                          type="button"
                          disabled={disabled}
                          aria-label={`Remove ${
                            visibleSelectedValues.length -
                            responsiveSettings.maxCount
                          } extra selected options`}
                          className="pointer-events-auto ml-2 flex shrink-0 disabled:cursor-not-allowed"
                          onClick={(event) => {
                            event.stopPropagation();
                            clearExtraOptions();
                          }}
                          onKeyDown={stopNestedActionKeyDown}
                        >
                          <XIcon
                            className={cn(
                              "h-4 w-4 cursor-pointer",
                              responsiveSettings.compactMode && "ml-1 h-3 w-3",
                            )}
                          />
                        </button>
                      </Badge>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center justify-between">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClear();
                      }}
                      onKeyDown={stopNestedActionKeyDown}
                      aria-label={`Clear all ${selectedValues.length} selected options`}
                      className="text-muted-foreground hover:text-foreground focus:ring-ring pointer-events-auto mx-2 flex h-4 w-4 cursor-pointer items-center justify-center focus:ring-2 focus:ring-offset-1 focus:outline-none"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                    <Separator
                      orientation="vertical"
                      className="flex h-full min-h-6"
                    />
                    <ChevronDown
                      className="text-muted-foreground mx-2 h-4 cursor-pointer"
                      aria-hidden="true"
                    />
                  </div>
                </>
              ) : (
                <div className="mx-auto flex w-full items-center justify-between">
                  <span className="text-muted-foreground mx-3 text-sm">
                    {placeholder}
                  </span>
                  <ChevronDown className="text-muted-foreground mx-2 h-4 cursor-pointer" />
                </div>
              )}
            </div>
          </div>
          <PopoverContent
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            aria-label="Available options"
            className={cn(
              "w-auto p-0",
              getPopoverAnimationClass(),
              screenSize === "mobile" && "w-[85vw] max-w-[280px]",
              screenSize === "tablet" && "w-[70vw] max-w-md",
              screenSize === "desktop" && "min-w-[300px]",
              popoverClassName,
            )}
            style={{
              animationDuration: `${animationConfig?.duration || animation}s`,
              animationDelay: `${animationConfig?.delay || 0}s`,
              maxWidth: `min(${widthConstraints.maxWidth}, 85vw)`,
              maxHeight: screenSize === "mobile" ? "70vh" : "60vh",
              touchAction: "manipulation",
            }}
            align="start"
            onEscapeKeyDown={() => setIsPopoverOpen(false)}
          >
            <Command shouldFilter={false}>
              {searchable && (
                <CommandInput
                  placeholder="Search options..."
                  onKeyDown={handleInputKeyDown}
                  value={searchValue}
                  onValueChange={setSearchValue}
                  aria-label="Search through available options"
                  aria-describedby={`${multiSelectId}-search-help`}
                />
              )}
              {searchable && (
                <div id={`${multiSelectId}-search-help`} className="sr-only">
                  Type to filter options. Use arrow keys to navigate results.
                </div>
              )}
              <CommandList
                className={cn(
                  "multiselect-scrollbar max-h-[40vh] overflow-y-auto",
                  screenSize === "mobile" && "max-h-[50vh]",
                  "overscroll-behavior-y-contain",
                )}
              >
                {filteredOptionCount === 0 && !canCreateFromSearch && (
                  <div
                    data-slot="command-empty"
                    className="py-6 text-center text-sm"
                  >
                    {emptyIndicator || "No results found."}
                  </div>
                )}{" "}
                {canCreateFromSearch && (
                  <CommandGroup>
                    <CommandItem
                      key={`__create__${trimmedSearchValue}`}
                      value={trimmedSearchValue}
                      onSelect={handleCreate}
                      role="option"
                      aria-label={`Create new option "${trimmedSearchValue}"`}
                      className="cursor-pointer"
                    >
                      <PlusIcon
                        className="text-muted-foreground mr-2 h-4 w-4"
                        aria-hidden="true"
                      />
                      <span>
                        Create &ldquo;
                        <span className="font-medium">
                          {trimmedSearchValue}
                        </span>
                        &rdquo;
                      </span>
                    </CommandItem>
                  </CommandGroup>
                )}
                {!hideSelectAll && !searchValue && (
                  <CommandGroup>
                    <CommandItem
                      key="all"
                      onSelect={toggleAll}
                      role="option"
                      aria-selected={
                        selectedValues.length ===
                        getAllOptions().filter((opt) => !opt.disabled).length
                      }
                      aria-label={`Select all ${
                        getAllOptions().length
                      } options`}
                      className="cursor-pointer"
                    >
                      <div
                        className={cn(
                          "border-primary mr-2 flex h-4 w-4 items-center justify-center border",
                          selectedValues.length ===
                            getAllOptions().filter((opt) => !opt.disabled)
                              .length
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                        aria-hidden="true"
                      >
                        <CheckIcon className="h-4 w-4 text-current" />
                      </div>
                      <span>
                        (Select All
                        {getAllOptions().length > 20
                          ? ` - ${getAllOptions().length} options`
                          : ""}
                        )
                      </span>
                    </CommandItem>
                  </CommandGroup>
                )}
                {isGroupedOptions(filteredOptions) ? (
                  filteredOptions.map((group) => {
                    const GroupIcon = group.icon;
                    return (
                      <CommandGroup
                        key={group.heading}
                        heading={
                          <span className="flex items-center gap-1.5">
                            {GroupIcon && <GroupIcon className="h-3.5 w-3.5" />}
                            {group.heading}
                          </span>
                        }
                      >
                        {group.options.map((option) => (
                          <MultiSelectOptionItem
                            key={option.value}
                            option={option}
                            isSelected={selectedValues.includes(option.value)}
                            onToggle={() => toggleOption(option.value)}
                          />
                        ))}
                      </CommandGroup>
                    );
                  })
                ) : (
                  <CommandGroup>
                    {filteredOptions.map((option) => (
                      <MultiSelectOptionItem
                        key={option.value}
                        option={option}
                        isSelected={selectedValues.includes(option.value)}
                        onToggle={() => toggleOption(option.value)}
                      />
                    ))}
                  </CommandGroup>
                )}
                <CommandSeparator />
                <CommandGroup>
                  <div className="flex items-center justify-between">
                    {selectedValues.length > 0 && (
                      <>
                        <CommandItem
                          onSelect={handleClear}
                          className="flex-1 cursor-pointer justify-center"
                        >
                          Clear
                        </CommandItem>
                        <Separator
                          orientation="vertical"
                          className="flex h-full min-h-6"
                        />
                      </>
                    )}
                    <CommandItem
                      onSelect={() => setIsPopoverOpen(false)}
                      className="max-w-full flex-1 cursor-pointer justify-center"
                    >
                      Close
                    </CommandItem>
                  </div>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
          {animation > 0 && selectedValues.length > 0 && (
            <WandSparkles
              className={cn(
                "text-foreground bg-background my-2 h-3 w-3 cursor-pointer",
                isAnimating ? "" : "text-muted-foreground",
              )}
              onClick={() => setIsAnimating(!isAnimating)}
            />
          )}
        </Popover>
      </>
    );
  },
);

MultiSelect.displayName = "MultiSelect";
export type { MultiSelectOption, MultiSelectGroup, MultiSelectProps };
